import { useEffect, useState, useCallback } from "react";

/**
 * @typedef {object} UseCountDownReturn
 * @prop {number} timeLeft
 * @prop {(timeLeft: number) => void} start
 * @prop {() => void} stop
 */

/**
 * Provides functions to start and stop a countdown,
 * and a variable updated every second with the remaining time
 * @returns {UseCountDownReturn}
 */
function useCountDown() {
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const start = useCallback(
    /** @param {number} initialTime */
    (initialTime) => {
      setTimeLeft(initialTime);
      setIsCountingDown(true);
    },
    [],
  );

  const stop = useCallback(() => {
    setIsCountingDown(false);
    setTimeLeft(0);
  }, []);

  useEffect(() => {
    setIsCountingDown(timeLeft > 0);
  }, [timeLeft]);

  useEffect(() => {
    let interval = null;
    if (isCountingDown) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            setIsCountingDown(false);
          }
          return Math.max(next, 0);
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isCountingDown]);

  return {
    timeLeft,
    start,
    stop,
  };
}

export default useCountDown;
